import type { Token, TokenData } from './types';
import { compareDesc, parseISO } from 'date-fns';
import { TEN_MINUTES_IN_MS } from '@/constants/session';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';

class TokenService {
  public static setTokenData({ token, refreshToken, expiresAt, userInactivityPeriod, userName }: TokenData) {
    if (!token || !refreshToken) return;

    localStorage.setItem(LocalStorageVariables.Token, token);
    localStorage.setItem(LocalStorageVariables.RefreshToken, refreshToken);
    localStorage.setItem(LocalStorageVariables.ExpiresAt, expiresAt);
    localStorage.setItem(LocalStorageVariables.UserInactivityPeriod, userInactivityPeriod);
    if (userName) localStorage.setItem(LocalStorageVariables.UserName, userName);
  }

  public static getUserName(): Token {
    return localStorage.getItem(LocalStorageVariables.UserName);
  }

  public static isPasswordChangeRequired() {
    return localStorage.getItem(LocalStorageVariables.IsRequiredPasswordChange || null);
  }

  public static getTokenRaw(): Token {
    return localStorage.getItem(LocalStorageVariables.Token) || null;
  }

  public static getRefreshTokenRaw(): Token {
    return localStorage.getItem(LocalStorageVariables.RefreshToken) || null;
  }

  public static checkTokenIsExpired(): boolean {
    const expiresAtISO = localStorage.getItem(LocalStorageVariables.ExpiresAt);

    if (!expiresAtISO) return true;

    const expiresAt = parseISO(expiresAtISO);

    return compareDesc(new Date(), expiresAt) <= 0;
  }

  public static isTokenExpiringSoon(): boolean {
    const expiresAtISO = localStorage.getItem(LocalStorageVariables.ExpiresAt);

    if (!expiresAtISO) return false;

    const expiresAt = parseISO(expiresAtISO);
    const currentTime = new Date();

    return expiresAt.getTime() - currentTime.getTime() <= TEN_MINUTES_IN_MS;
  }

  public static getTokenData(): Token {
    const expiresAtISO = localStorage.getItem(LocalStorageVariables.ExpiresAt);

    if (!expiresAtISO) return null;

    const expiresAt = parseISO(expiresAtISO);
    const isFresh = compareDesc(new Date(), expiresAt) > 0;

    if (!isFresh) return null;

    return localStorage.getItem(LocalStorageVariables.Token);
  }
}

export default TokenService;
